import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Segmented } from '@verlo/shared/components/ui/segmented';
import { Signal } from '@verlo/shared/components/ui/signal';
import { Notice } from '@verlo/shared/components/ui/notice';
import { cashApi } from '@verlo/shared/services/api';
import { useApp } from '@verlo/shared/contexts/AppContext';
import { useSite } from '../../../contexts/SiteContext';
import ReportsLayout from '../../../components/reports/ReportsLayout';
import { PageHeader, Stat, Stats, DataTable, FilterBar } from '../../../components/office';

/**
 * Reports › Money › Cash-ups (cash-off wave 2, 18 Sep 2026). Every drawer the tills
 * counted in the period — declared against expected, the variance in its tone with the
 * reason, the PDQ against the till, tips out, what went to the safe, who signed — and the
 * safe's ledger with its running balance and the bankings. Node-owned rows the Cloud
 * reads; nothing here writes.
 */

const RANGES = [
  { value: '7', label: 'Last 7 days' },
  { value: '28', label: 'Last 4 weeks' },
  { value: '91', label: 'Last 13 weeks' },
];
const iso = (d) => d.toISOString().slice(0, 10);
const dayLabel = (s) => { try { return new Date(s + 'T12:00:00').toLocaleDateString('en-GB', { weekday: 'short', day: 'numeric', month: 'short' }); } catch { return s; } };
const when = (s) => { try { return new Date(s).toLocaleString('en-GB', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' }); } catch { return s; } };

const SAFE_KIND = {
  drop_in: { label: 'Drop in', tone: 'ok', sign: '+' },
  float_out: { label: 'Float out', tone: 'info', sign: '−' },
  petty_cash_out: { label: 'Petty cash', tone: 'warn', sign: '−' },
  banking_out: { label: 'Banked', tone: 'info', sign: '−' },
  adjust: { label: 'Adjusted', tone: 'warn', sign: '' },
};

export const varianceSignal = (v, threshold = 5, fmt = (n) => `£${n.toFixed(2)}`) => {
  const pence = Math.round((v || 0) * 100);
  if (pence === 0) return { tone: 'ok', text: 'Balanced' };
  const over = Math.abs(pence) > Math.round(threshold * 100);
  return { tone: over ? 'stop' : 'warn', text: `${pence > 0 ? 'Over' : 'Short'} ${fmt(Math.abs(v))}` };
};

const CashUpsPage = () => {
  const navigate = useNavigate();
  const { formatCurrency } = useApp();
  const { site } = useSite();
  const [params, setParams] = useSearchParams();
  const view = params.get('view') === 'safe' ? 'safe' : 'cashups';
  // Sent here about a till open since a given day (Today's Investigate, `?since=`): the
  // shortest range that has it, or one from that day (Chris's review H4).
  const since = params.get('since');
  const [range, setRange] = useState(() => {
    if (!since || Number.isNaN(Date.parse(since))) return '28';
    const need = Math.ceil((Date.now() - Date.parse(`${since}T00:00:00`)) / 864e5) + 1;
    return RANGES.find((r) => Number(r.value) >= need)?.value || String(need);
  });
  const ranges = RANGES.some((r) => r.value === range) ? RANGES : [...RANGES, { value: range, label: `Since ${dayLabel(since)}` }];
  const [summary, setSummary] = useState(null);
  const [sessions, setSessions] = useState([]);
  const [safe, setSafe] = useState(null);
  const [loading, setLoading] = useState(true);

  const dates = useMemo(() => {
    const to = new Date();
    const from = new Date(to.getTime() - (parseInt(range, 10) - 1) * 864e5);
    return { date_from: iso(from), date_to: iso(to) };
  }, [range]);

  useEffect(() => {
    let live = true;
    setLoading(true);
    const p = { ...dates, ...(site ? { site } : {}) };
    Promise.all([
      cashApi.summary(p).catch(() => ({ data: null })),
      cashApi.sessions({ ...p, limit: 500 }).catch(() => ({ data: { sessions: [] } })),
      cashApi.safe({ ...(site ? { site } : {}), limit: 200 }).catch(() => ({ data: null })),
    ]).then(([s, l, sf]) => {
      if (!live) return;
      setSummary(s.data); setSessions(l.data?.sessions || []); setSafe(sf.data);
    }).finally(() => { if (live) setLoading(false); });
    return () => { live = false; };
  }, [dates, site]);

  const threshold = summary?.threshold ?? 5;
  const closed = sessions.filter((s) => s.status === 'closed');
  const open = sessions.filter((s) => s.status === 'open');
  const variance = varianceSignal(summary?.variance, threshold, formatCurrency);

  const columns = [
    { key: 'trading_date', label: 'Day', sortable: true, className: 'whitespace-nowrap', render: (r) => <span className="whitespace-nowrap font-medium text-ink">{dayLabel(r.trading_date)}</span> },
    { key: 'terminal_label', label: 'Till', sortable: true, render: (r) => <span className="whitespace-nowrap text-ink-soft">{r.terminal_label || r.terminal_id}{r.z_number ? <span className="ml-1.5 text-ink-faint">Z {r.z_number}</span> : null}</span> },
    { key: 'expected_cash', label: 'Expected', align: 'right', mono: true, render: (r) => formatCurrency(r.expected_cash) },
    { key: 'declared_cash', label: 'Counted', align: 'right', mono: true, render: (r) => formatCurrency(r.declared_cash) },
    { key: 'variance', label: 'Variance', sortable: true, sortValue: (r) => Math.abs(r.variance || 0), render: (r) => { const v = varianceSignal(r.variance, threshold, formatCurrency); return <Signal tone={v.tone} className="whitespace-nowrap">{v.text}</Signal>; } },
    { key: 'variance_reason', label: 'Reason', className: 'min-w-[14rem]', render: (r) => <span className="text-ink-soft">{r.variance_reason || ''}</span> },
    { key: 'pdq_variance', label: 'PDQ', align: 'right', mono: true, render: (r) => r.pdq_variance == null ? <span className="text-ink-faint">—</span> : Math.round(r.pdq_variance * 100) === 0 ? <span className="text-signal-ok">matches</span> : <span className={Math.abs(r.pdq_variance) > threshold ? 'text-signal-stop' : 'text-signal-warn'}>{r.pdq_variance > 0 ? '+' : '−'}{formatCurrency(Math.abs(r.pdq_variance))}</span> },
    { key: 'tips_out', label: 'Tips out', align: 'right', mono: true, render: (r) => formatCurrency(r.tips_out) },
    { key: 'to_safe', label: 'To safe', align: 'right', mono: true, render: (r) => formatCurrency(r.to_safe) },
    { key: 'closed_by_name', label: 'Signed off', render: (r) => <span className="text-ink-soft">{r.closed_by_name || ''}</span> },
  ];

  const safeColumns = [
    { key: 'created_at', label: 'When', sortable: true, render: (r) => <span className="text-ink-soft">{when(r.created_at)}</span> },
    { key: 'kind', label: 'Movement', render: (r) => { const k = SAFE_KIND[r.kind] || { label: r.kind, tone: 'info' }; return <Signal tone={k.tone} dot={false}>{k.label}</Signal>; } },
    { key: 'amount', label: 'Amount', align: 'right', mono: true, render: (r) => `${(SAFE_KIND[r.kind] || {}).sign || ''}${formatCurrency(Math.abs(r.amount))}` },
    { key: 'balance_after', label: 'Balance after', align: 'right', mono: true, render: (r) => <span className="font-semibold text-ink">{formatCurrency(r.balance_after)}</span> },
    { key: 'note', label: 'Note', render: (r) => <span className="text-ink-soft">{r.bag_ref ? <span className="font-mono text-ink">{r.bag_ref}</span> : null}{r.bag_ref && r.note ? ' · ' : ''}{r.note}</span> },
    { key: 'by_name', label: 'By', render: (r) => <span className="text-ink-soft">{r.by_name || ''}</span> },
  ];

  return (
    <ReportsLayout>
      <PageHeader
        title="Cash-ups"
        purpose="Every drawer counted, what it should have held, and where the difference went."
        tabs={(
          <Segmented value={view} onChange={(v) => setParams(v === 'safe' ? { view: 'safe' } : {})} options={[
            { value: 'cashups', label: 'Cash-ups', testId: 'cash-ups-tab-cashups' },
            { value: 'safe', label: 'The safe', testId: 'cash-ups-tab-safe' },
          ]} />
        )}
        testId="cash-ups-header"
      />

      {view === 'cashups' ? (
        <>
          <FilterBar right={<span>{closed.length} cash-up{closed.length === 1 ? '' : 's'}{open.length ? ` · ${open.length} ${open.length === 1 ? 'till' : 'tills'} open` : ''}</span>} testId="cash-ups-filters">
            <Segmented value={range} onChange={setRange} options={ranges.map((r) => ({ ...r, testId: `cash-ups-range-${r.value}` }))} />
          </FilterBar>

          {open.some((s) => s.trading_date < iso(new Date())) && (
            <Notice tone="warn" word="Till" title="A till has not been cashed off" className="mb-4" data-testid="cash-ups-open-notice">
              {[...new Set(open.filter((s) => s.trading_date < iso(new Date())).map((s) => `${s.terminal_label || s.terminal_id} since ${dayLabel(s.trading_date)}`))].join(' · ')}. It closes on the till.
            </Notice>
          )}

          <Stats className="mb-6" data-testid="cash-ups-tiles">
            <Stat label="Counted" value={formatCurrency(summary?.declared)} hint={`expected ${formatCurrency(summary?.expected)}`} testId="cash-ups-declared" />
            <Stat label="Variance" value={summary ? `${(summary.variance || 0) > 0 ? '+' : (summary.variance || 0) < 0 ? '−' : ''}${formatCurrency(Math.abs(summary.variance || 0))}` : '—'} delta={summary ? { text: variance.text.split(' ')[0], tone: variance.tone } : undefined} hint={summary ? `short ${formatCurrency(summary.short)} · over ${formatCurrency(summary.over)}` : undefined} testId="cash-ups-variance" />
            <Stat label="Over the threshold" value={summary?.over_threshold ?? '—'} delta={summary?.over_threshold ? { text: `of ${summary.sessions} · over ${formatCurrency(threshold)}`, tone: 'warn' } : undefined} hint={!summary?.over_threshold && summary ? `none over ${formatCurrency(threshold)}` : undefined} testId="cash-ups-over-threshold" />
            <Stat label="PDQ vs till" value={summary?.pdq_variance == null ? '—' : `${summary.pdq_variance > 0 ? '+' : summary.pdq_variance < 0 ? '−' : ''}${formatCurrency(Math.abs(summary.pdq_variance))}`} hint={summary ? `${summary.pdq_entered} of ${summary.sessions} entered` : undefined} testId="cash-ups-pdq" />
            <Stat label="In the safe" value={formatCurrency(safe?.balance)} delta={safe?.over_limit ? { text: `over the ${formatCurrency(safe.limit)} limit`, tone: 'warn' } : undefined} hint={summary ? `${formatCurrency(summary.to_safe)} dropped in the period` : undefined} onClick={() => setParams({ view: 'safe' })} testId="cash-ups-safe" />
          </Stats>

          <DataTable
            columns={columns}
            rows={closed}
            rowKey="id"
            loading={loading}
            defaultSort={{ key: 'trading_date', dir: 'desc' }}
            onRowClick={(r) => navigate(`/back-office/reports/cash-ups/${r.id}`)}
            empty={{ word: 'Cash-ups', title: 'No drawer counted in this period', children: 'Cash-ups appear here once a till is cashed off.' }}
            testId="cash-ups-table"
          />
        </>
      ) : (
        <>
          <Stats className="mb-6" data-testid="safe-tiles">
            <Stat label="In the safe" value={formatCurrency(safe?.balance)} delta={safe?.over_limit ? { text: 'Over the limit: time to bank', tone: 'warn' } : safe ? { text: 'Within the limit', tone: 'ok' } : undefined} hint={safe ? `limit ${formatCurrency(safe.limit)}${safe.movements?.[0] ? ` · as of ${when(safe.movements[0].created_at)}, the till's last word` : ''}` : undefined} size="lg" testId="safe-balance" />
            <Stat label="Last banked" value={(() => { const b = (safe?.movements || []).find((m) => m.kind === 'banking_out'); return b ? formatCurrency(b.amount) : '—'; })()} hint={(() => { const b = (safe?.movements || []).find((m) => m.kind === 'banking_out'); return b ? `${when(b.created_at)}${b.bag_ref ? ` · ${b.bag_ref}` : ''}` : 'no banking recorded'; })()} testId="safe-last-banked" />
            <Stat label="Petty cash out" value={formatCurrency((safe?.movements || []).filter((m) => m.kind === 'petty_cash_out').reduce((a, m) => a + (m.amount || 0), 0))} hint="in the movements shown" testId="safe-petty" />
          </Stats>
          {safe?.over_limit && (
            <Notice tone="warn" word="Safe" title={`The safe holds ${formatCurrency(safe.balance)} against a limit of ${formatCurrency(safe.limit)}`} className="mb-4" data-testid="safe-over-notice">
              Banking is recorded on the till, with the bag reference.
            </Notice>
          )}
          <DataTable
            columns={safeColumns}
            rows={safe?.movements || []}
            rowKey="id"
            loading={loading}
            empty={{ word: 'Safe', title: 'Nothing in the safe ledger yet', children: 'Drops from the tills, petty cash and bankings show here with a running balance.' }}
            testId="safe-table"
          />
        </>
      )}
    </ReportsLayout>
  );
};

export default CashUpsPage;
